import { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AccommodationRoomForm } from "Bands/components/AccommodationRoomForm/AccommodationRoomForm";
import { clearDataByKey, dataMetaSelector } from "Bands/state";
import { editEventAccommodationRoomType } from "Bands/state/data/actions/eventBlocks/eventAccommodationRoomType";
import { BAND_ACTIONS, IAccommodationRoomType, IAccommodationRoomTypeParams } from "Bands/types";
import { useClearActionMetaOnUnMount } from "Bands/utils";
import { copy } from "shared/assets";
import { Modal } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { IAPIState, IDataKeys, IFormButtons, IStatus } from "shared/types";
import { dispatchGetEventAccommodationRoomsForBlock } from "Bands/state/data/actions/eventBlocks/eventAccommodationRoom";
import { dispatchGetEventAccommodationReservations } from "Bands/state/data/actions/eventBlocks/eventAccommodationReservation";

const { accommodationBlock: blockCopy } = copy;

interface EditAccommodationRoomProps {
  blockId: string;
  accommodationRoom: IAccommodationRoomType;
  handleClose: () => void;
}

export const EditAccommodationRoomModal: FC<EditAccommodationRoomProps> = ({
  blockId,
  accommodationRoom,
  handleClose,
}) => {
  const dispatch = useDispatch();

  const { api: apiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.editAccommodationRoomType));

  useClearActionMetaOnUnMount(BAND_ACTIONS.editAccommodationRoomType);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatchGetEventAccommodationReservations({
        dispatch,
        blockId,
        page: 1,
      });

      dispatchGetEventAccommodationRoomsForBlock(dispatch, blockId);

      handleClose();

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: blockCopy.create.roomsBlock.messages.edit,
        })
      );
    }
  }, [apiMeta?.status, blockId, dispatch, handleClose]);

  const onSubmit = (editedAccommodationRoom: IAccommodationRoomTypeParams) => {
    dispatch(clearDataByKey(IDataKeys.eventAccommodationRoom));
    dispatch(
      editEventAccommodationRoomType({
        id: accommodationRoom.id,
        data: editedAccommodationRoom,
      })
    );
  };

  const buttons: IFormButtons = {
    primary: {
      children: "Save",
      "data-testid": "edit_accommodation_room_submit",
    },
    secondary: {
      children: "Cancel",
      onClick: handleClose,
    },
  };

  return (
    <Modal
      title={"Editing room"}
      handleClose={handleClose}
      data-testid="edit_accommodation_room_modal"
    >
      <AccommodationRoomForm
        buttons={buttons}
        handleSubmit={onSubmit}
        defaultValues={accommodationRoom}
        isSubmitting={apiMeta?.status === IAPIState.pending}
        apiError={apiMeta?.error}
        error={!!apiMeta?.error}
        minimumQuantity={accommodationRoom.minimumQuantity}
        minimumPeoplePerRoom={accommodationRoom.minimumPeoplePerRoom}
      />
    </Modal>
  );
};
